import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../App";
import axios from "axios";
import { 
  Plus, Trash2, Edit2, Calendar, CreditCard, Building2, User, FileText, 
  AlertTriangle, Check, Clock, Ban, ChevronRight, Filter
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const kategoriler = [
  { value: "tedarikci", label: "Tedarikçi", icon: Building2, color: "#002FA7" },
  { value: "personel", label: "Personel Maaşı", icon: User, color: "#00C566" },
  { value: "fatura", label: "Fatura", icon: FileText, color: "#FF9500" },
  { value: "kira", label: "Kira", icon: Building2, color: "#9333EA" },
  { value: "vergi", label: "Vergi", icon: FileText, color: "#FF3B30" },
  { value: "diger", label: "Diğer", icon: CreditCard, color: "#5E5E62" }
];

const odemeYontemleri = [
  { value: "havale", label: "Havale/EFT" },
  { value: "nakit", label: "Nakit" },
  { value: "cek", label: "Çek" },
  { value: "kredi_karti", label: "Kredi Kartı" }
];

const durumlar = [
  { value: "bekliyor", label: "Bekliyor", color: "#FF9500", bgColor: "#FFF4E5" },
  { value: "onaylandi", label: "Onaylandı", color: "#002FA7", bgColor: "#E8EDFA" },
  { value: "odendi", label: "Ödendi", color: "#00C566", bgColor: "#E6F9F0" },
  { value: "iptal", label: "İptal", color: "#FF3B30", bgColor: "#FFEBEE" }
];

const OdemeYonetimi = () => {
  const [odemeler, setOdemeler] = useState([]);
  const [ozet, setOzet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterKategori, setFilterKategori] = useState("hepsi");
  const [filterDurum, setFilterDurum] = useState("bekliyor");
  const [form, setForm] = useState({
    alici: "",
    kategori: "tedarikci",
    tutar: "",
    vade_tarihi: new Date(),
    odeme_yontemi: "havale",
    aciklama: "",
    banka_hesabi: "",
    fatura_no: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [odemelerRes, ozetRes] = await Promise.all([
        axios.get(`${API}/odemeler`, { withCredentials: true }),
        axios.get(`${API}/odemeler/ozet`, { withCredentials: true })
      ]);
      setOdemeler(odemelerRes.data);
      setOzet(ozetRes.data);
    } catch (error) {
      toast.error("Veriler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.alici || !form.tutar) {
      toast.error("Lütfen gerekli alanları doldurun");
      return;
    }

    try {
      const payload = {
        ...form,
        tutar: parseFloat(form.tutar),
        vade_tarihi: form.vade_tarihi.toISOString()
      };

      if (editingId) {
        await axios.put(`${API}/odemeler/${editingId}`, payload, { withCredentials: true });
        toast.success("Ödeme güncellendi");
      } else {
        await axios.post(`${API}/odemeler`, payload, { withCredentials: true });
        toast.success("Ödeme eklendi");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("İşlem sırasında hata oluştu");
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      alici: item.alici,
      kategori: item.kategori,
      tutar: item.tutar.toString(),
      vade_tarihi: new Date(item.vade_tarihi),
      odeme_yontemi: item.odeme_yontemi,
      aciklama: item.aciklama || "",
      banka_hesabi: item.banka_hesabi || "",
      fatura_no: item.fatura_no || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu ödemeyi silmek istediğinize emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/odemeler/${id}`, { withCredentials: true });
      toast.success("Ödeme silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const handleDurumChange = async (id, yeniDurum) => {
    try {
      await axios.put(`${API}/odemeler/${id}/durum`, { durum: yeniDurum }, { withCredentials: true });
      toast.success(`Ödeme durumu güncellendi`);
      fetchData();
    } catch (error) {
      toast.error("Durum güncellenemedi");
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      alici: "",
      kategori: "tedarikci",
      tutar: "",
      vade_tarihi: new Date(),
      odeme_yontemi: "havale",
      aciklama: "",
      banka_hesabi: "",
      fatura_no: ""
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(value || 0);
  };

  const getKategoriInfo = (kategori) => {
    return kategoriler.find(k => k.value === kategori) || kategoriler[5];
  };

  const getDurumInfo = (durum) => {
    return durumlar.find(d => d.value === durum) || durumlar[0];
  };

  const isOverdue = (vadeTarihi, durum) => {
    if (durum === "odendi" || durum === "iptal") return false;
    return new Date(vadeTarihi) < new Date();
  };

  const getDaysUntil = (vadeTarihi) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const vade = new Date(vadeTarihi);
    vade.setHours(0, 0, 0, 0);
    const diff = Math.ceil((vade - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Filter payments
  let filteredOdemeler = odemeler;
  if (filterKategori !== "hepsi") {
    filteredOdemeler = filteredOdemeler.filter(o => o.kategori === filterKategori);
  }
  if (filterDurum !== "hepsi") {
    filteredOdemeler = filteredOdemeler.filter(o => o.durum === filterDurum);
  }

  if (loading) {
    return (
      <Layout>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="odeme-yonetimi-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-[#0A0A0B] tracking-tight">
              Ödeme Yönetimi
            </h1>
            <p className="text-[#5E5E62] text-sm mt-1">
              Tüm ödemelerinizi planlayın ve takip edin
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <button className="btn-primary flex items-center gap-2" data-testid="add-odeme-btn">
                <Plus size={18} />
                Yeni Ödeme Planla
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[550px]">
              <DialogHeader>
                <DialogTitle className="font-heading">{editingId ? "Ödeme Düzenle" : "Yeni Ödeme Ekle"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Kategori Selection */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-2">Kategori</label>
                  <div className="grid grid-cols-3 gap-2">
                    {kategoriler.map((kat) => (
                      <button
                        key={kat.value}
                        type="button"
                        onClick={() => setForm({ ...form, kategori: kat.value })}
                        className={`p-3 rounded-sm border-2 transition-colors text-center ${
                          form.kategori === kat.value
                            ? `border-[${kat.color}] bg-[${kat.color}]/10`
                            : "border-[#E4E4E7] hover:border-[#D4D4D8]"
                        }`}
                        style={form.kategori === kat.value ? { borderColor: kat.color, backgroundColor: `${kat.color}10` } : {}}
                      >
                        <kat.icon size={20} className="mx-auto mb-1" style={{ color: kat.color }} />
                        <span className="text-xs font-medium">{kat.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Alıcı */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Alıcı / Ödeme Yapılacak Yer</label>
                  <input
                    type="text"
                    value={form.alici}
                    onChange={(e) => setForm({ ...form, alici: e.target.value })}
                    className="input-default"
                    placeholder="Örn: ABC Tedarik Ltd., Ahmet Yılmaz, Turkcell"
                    data-testid="alici-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Tutar */}
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Tutar (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.tutar}
                      onChange={(e) => setForm({ ...form, tutar: e.target.value })}
                      className="input-default"
                      placeholder="0.00"
                      data-testid="tutar-input"
                    />
                  </div>

                  {/* Vade Tarihi */}
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Vade Tarihi</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="input-default text-left flex items-center gap-2" data-testid="vade-picker">
                          <Calendar size={16} className="text-[#5E5E62]" />
                          {format(form.vade_tarihi, "d MMM yyyy", { locale: tr })}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={form.vade_tarihi}
                          onSelect={(date) => date && setForm({ ...form, vade_tarihi: date })}
                          locale={tr}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Ödeme Yöntemi */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Ödeme Yöntemi</label>
                  <Select value={form.odeme_yontemi} onValueChange={(value) => setForm({ ...form, odeme_yontemi: value })}>
                    <SelectTrigger data-testid="odeme-yontemi-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {odemeYontemleri.map((y) => (
                        <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Banka Hesabı / Fatura No */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Banka/IBAN (Opsiyonel)</label>
                    <input
                      type="text"
                      value={form.banka_hesabi}
                      onChange={(e) => setForm({ ...form, banka_hesabi: e.target.value })}
                      className="input-default"
                      placeholder="TR..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Fatura No (Opsiyonel)</label>
                    <input
                      type="text"
                      value={form.fatura_no}
                      onChange={(e) => setForm({ ...form, fatura_no: e.target.value })}
                      className="input-default"
                      placeholder="FAT-2024-001"
                    />
                  </div>
                </div>

                {/* Açıklama */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Açıklama</label>
                  <input
                    type="text"
                    value={form.aciklama}
                    onChange={(e) => setForm({ ...form, aciklama: e.target.value })}
                    className="input-default"
                    placeholder="Opsiyonel not"
                  />
                </div>

                <button type="submit" className="btn-primary w-full" data-testid="submit-odeme-btn">
                  {editingId ? "Güncelle" : "Ödeme Planla"}
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className={`bg-white border rounded-sm p-4 ${ozet?.geciken_adet > 0 ? 'border-[#FF3B30] bg-[#FFEBEE]' : 'border-[#E4E4E7]'}`}>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={18} className="text-[#FF3B30]" />
              <span className="text-sm text-[#5E5E62]">Geciken Ödemeler</span>
            </div>
            <p className="font-mono text-xl font-medium text-[#FF3B30]">{formatCurrency(ozet?.geciken_toplam)}</p>
            <p className="text-xs text-[#FF3B30] mt-1">{ozet?.geciken_adet || 0} adet</p>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={18} className="text-[#FF9500]" />
              <span className="text-sm text-[#5E5E62]">Bugün Ödenecek</span>
            </div>
            <p className="font-mono text-xl font-medium text-[#FF9500]">{formatCurrency(ozet?.bugun_toplam)}</p>
            <p className="text-xs text-[#5E5E62] mt-1">{ozet?.bugun_adet || 0} adet</p>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar size={18} className="text-[#002FA7]" />
              <span className="text-sm text-[#5E5E62]">Bu Hafta</span>
            </div>
            <p className="font-mono text-xl font-medium text-[#002FA7]">{formatCurrency(ozet?.bu_hafta_toplam)}</p>
            <p className="text-xs text-[#5E5E62] mt-1">{ozet?.bu_hafta_adet || 0} adet</p>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard size={18} className="text-[#5E5E62]" />
              <span className="text-sm text-[#5E5E62]">Toplam Bekleyen</span>
            </div>
            <p className="font-mono text-xl font-medium text-[#0A0A0B]">{formatCurrency(ozet?.toplam_bekleyen)}</p>
            <p className="text-xs text-[#5E5E62] mt-1">{ozet?.bekleyen_adet || 0} adet</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-[#5E5E62]" />
            <span className="text-sm text-[#5E5E62]">Filtre:</span>
          </div>
          <Select value={filterDurum} onValueChange={setFilterDurum}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hepsi">Tüm Durumlar</SelectItem>
              {durumlar.map((d) => (
                <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterKategori} onValueChange={setFilterKategori}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hepsi">Tüm Kategoriler</SelectItem>
              {kategoriler.map((k) => (
                <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Payments Table */}
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="odemeler-table">
              <thead>
                <tr className="border-b border-[#E4E4E7]">
                  <th className="table-header">Kategori</th>
                  <th className="table-header">Alıcı</th>
                  <th className="table-header">Tutar</th>
                  <th className="table-header">Vade Tarihi</th>
                  <th className="table-header">Ödeme Yöntemi</th>
                  <th className="table-header">Durum</th>
                  <th className="table-header w-32"></th>
                </tr>
              </thead>
              <tbody>
                {filteredOdemeler.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-[#5E5E62]">
                      Henüz ödeme bulunmuyor
                    </td>
                  </tr>
                ) : (
                  filteredOdemeler.map((item) => {
                    const katInfo = getKategoriInfo(item.kategori);
                    const durumInfo = getDurumInfo(item.durum);
                    const overdue = isOverdue(item.vade_tarihi, item.durum);
                    const daysUntil = getDaysUntil(item.vade_tarihi);
                    
                    return (
                      <tr 
                        key={item.id} 
                        className={`table-row ${overdue ? 'bg-[#FFEBEE]/50' : ''}`}
                      >
                        <td className="table-cell">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-8 h-8 rounded-sm flex items-center justify-center"
                              style={{ backgroundColor: `${katInfo.color}15` }}
                            >
                              <katInfo.icon size={16} style={{ color: katInfo.color }} />
                            </div>
                            <span className="text-sm">{katInfo.label}</span>
                          </div>
                        </td>
                        <td className="table-cell">
                          <div>
                            <p className="font-medium">{item.alici}</p>
                            {item.aciklama && (
                              <p className="text-xs text-[#5E5E62]">{item.aciklama}</p>
                            )}
                          </div>
                        </td>
                        <td className="table-cell font-mono font-medium text-[#0A0A0B]">
                          {formatCurrency(item.tutar)}
                        </td>
                        <td className="table-cell">
                          <div>
                            <p className={`font-mono ${overdue ? 'text-[#FF3B30]' : 'text-[#5E5E62]'}`}>
                              {new Date(item.vade_tarihi).toLocaleDateString('tr-TR')}
                            </p>
                            {item.durum !== "odendi" && item.durum !== "iptal" && (
                              <p className={`text-xs ${overdue ? 'text-[#FF3B30]' : daysUntil <= 3 ? 'text-[#FF9500]' : 'text-[#5E5E62]'}`}>
                                {overdue ? `${Math.abs(daysUntil)} gün gecikti` : 
                                 daysUntil === 0 ? 'Bugün' : 
                                 daysUntil === 1 ? 'Yarın' : 
                                 `${daysUntil} gün kaldı`}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="table-cell text-sm capitalize">{item.odeme_yontemi.replace('_', ' ')}</td>
                        <td className="table-cell">
                          <Select 
                            value={item.durum} 
                            onValueChange={(value) => handleDurumChange(item.id, value)}
                          >
                            <SelectTrigger 
                              className="w-[120px] h-8 text-xs"
                              style={{ backgroundColor: durumInfo.bgColor, borderColor: durumInfo.color }}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {durumlar.map((d) => (
                                <SelectItem key={d.value} value={d.value}>
                                  <span style={{ color: d.color }}>{d.label}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-2 text-[#5E5E62] hover:bg-[#F7F7F8] rounded-sm transition-colors"
                              data-testid={`edit-${item.id}`}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="p-2 text-[#FF3B30] hover:bg-[#FFEBEE] rounded-sm transition-colors"
                              data-testid={`delete-${item.id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OdemeYonetimi;
